import { isEmpty } from 'lodash-es';

import apiGw from '@/services/apiGw';
import { Address, AddressV2 } from '@/types/general';
import { AlertTypes, useAlertStore } from '@/store/alertsStore';

export default () => {
  const alertsStore = useAlertStore();

  async function addressValidation(address: Address): Promise<undefined | Address> {
    try {
      /**
       * Until we migrate all addresses to v2 (number & city instead of houseNo & town) this hack is required
       */
      await apiGw.post('/v2/postaladdress/verify', {
        ...address,
        ...{ number: address.houseNo, city: address.town, postalCode: address.zip }
      });
    } catch (error: any) {
      if (error.status === 422) {
        if (isEmpty(error.response.data)) {
          alertsStore.addAlert({
            id: 'address-changed-notfound',
            title: 'app.settings.address.message_1.title',
            message: 'app.settings.address.message_1.txt',
            dismissable: true,
            alertType: AlertTypes.Error,
            alertTimestamp: Date.now(),
            autoDismissDelay: 5000
          });
        } else {
          if (error.response.data.country) address.country = error.response.data.country;
          if (error.response.data.houseNo) address.houseNo = error.response.data.houseNo;
          if (error.response.data.street) address.street = error.response.data.street;
          if (error.response.data.zip) address.zip = error.response.data.zip;
          if (error.response.data.town) address.town = error.response.data.town;

          alertsStore.addAlert({
            id: 'address-changed-suggestion',
            title: 'app.settings.address.message_2.title',
            message: 'app.settings.address.message_2.txt',
            dismissable: true,
            alertType: AlertTypes.Warning,
            alertTimestamp: Date.now(),
            autoDismissDelay: 5000
          });
          return address;
        }
      } else {
        alertsStore.addAlert({
          id: 'address-changed',
          title: 'app.settings.address.message_1.title',
          message: 'app.settings.address.message_1.txt',
          dismissable: true,
          alertType: AlertTypes.Error,
          alertTimestamp: Date.now(),
          autoDismissDelay: 5000
        });
      }
      throw new Error();
    }
  }

  /**
   * Address validation for for Swiss addresses (if country is not CHE, no validation is done)
   *
   * @param address
   * @returns
   */
  async function addressValidationV2(address: AddressV2): Promise<undefined | AddressV2> {
    if (!address || address.country !== 'CHE') {
      return;
    }
    try {
      /**
       * Until we migrate all addresses to v2 (number & city instead of houseNo & town) this hack is required
       */
      await apiGw.post('/v2/postaladdress/verify', {
        ...address
      });
    } catch (error: any) {
      if (error.status === 422) {
        if (isEmpty(error.response.data)) {
          alertsStore.addAlert({
            id: 'address-changed-notfound',
            title: 'app.settings.address.message_1.title',
            message: 'app.settings.address.message_1.txt',
            dismissable: true,
            alertType: AlertTypes.Error,
            alertTimestamp: Date.now(),
            autoDismissDelay: 5000
          });
        } else {
          if (error.response.data.country) address.country = error.response.data.country;
          if (error.response.data.houseNo) address.number = error.response.data.houseNo;
          if (error.response.data.street) address.street = error.response.data.street;
          if (error.response.data.zip) address.postalCode = error.response.data.zip;
          if (error.response.data.town) address.city = error.response.data.town;

          alertsStore.addAlert({
            id: 'address-changed-suggestion',
            title: 'app.settings.address.message_2.title',
            message: 'app.settings.address.message_2.txt',
            dismissable: true,
            alertType: AlertTypes.Warning,
            alertTimestamp: Date.now(),
            autoDismissDelay: 5000
          });
          return address;
        }
      } else {
        alertsStore.addAlert({
          id: 'address-changed',
          title: 'app.settings.address.message_1.title',
          message: 'app.settings.address.message_1.txt',
          dismissable: true,
          alertType: AlertTypes.Error,
          alertTimestamp: Date.now(),
          autoDismissDelay: 5000
        });
      }
      throw new Error();
    }
  }

  async function zipValidation(zip: string, country = 'CHE') {
    try {
      const rsp = await apiGw.post('/v2/postalcode/expand', {
        city: '',
        country: country,
        postalcode: zip
      });
      return rsp.data;
    } catch (error) {
      alertsStore.removeAlert('technicalError-404');
      alertsStore.addAlert({
        id: 'zipValidationError-404',
        title: 'webapp.error.zipValidation.title',
        message: 'webapp.error.zipValidation.message',
        dismissable: true,
        alertType: AlertTypes.Error,
        alertTimestamp: Date.now(),
        autoDismissDelay: 5000
      });
      console.error('Something went wrong', error);
    }
  }
  return {
    addressValidation,
    addressValidationV2,
    zipValidation
  };
};
